/**
 * Activity-types API.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from './api-client';
import type {
  ActivityType,
  CreateActivityTypeDto,
  UpdateActivityTypeDto,
} from '@/types/activity-type.types';
import type { PaginatedResponse } from '@/types/data-table.types';

/** Append query params to a path when the search string is non-empty. */
function listUrl(path: string, searchParams: URLSearchParams): string {
  const q = searchParams.toString();
  return q ? `${path}?${q}` : path;
}

/** Trigger a browser download of an XLSX blob. */
function downloadXlsx(data: Blob, filename: string): void {
  const blob = new Blob([data], { type: XLSX_MIME });
  const url = globalThis.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
}

/** `GET /activity-types` — paginated activity types. */
export const getActivityTypes = async (params?: {
  page?: number;
  pageSize?: number;
  statuses?: string[];
  search?: string;
}): Promise<PaginatedResponse<ActivityType>> => {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.append('page', params.page.toString());
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  params?.statuses?.forEach((s) => searchParams.append('statuses', s));
  if (params?.search) searchParams.append('search', params.search);
  const { data } = await apiClient.get<PaginatedResponse<ActivityType>>(
    listUrl('/activity-types', searchParams),
  );
  return data;
};

/** `GET /activity-types/:id` */
export const getActivityType = async (id: string): Promise<ActivityType> => {
  const { data } = await apiClient.get(`/activity-types/${id}`);
  return data;
};

/** `POST /activity-types` */
export const createActivityType = async (dto: CreateActivityTypeDto): Promise<ActivityType> => {
  const { data } = await apiClient.post('/activity-types', dto);
  return data;
};

/** `PATCH /activity-types/:id` */
export const updateActivityType = async (
  id: string,
  dto: UpdateActivityTypeDto,
): Promise<ActivityType> => {
  const { data } = await apiClient.patch(`/activity-types/${id}`, dto);
  return data;
};

/** `DELETE /activity-types/:id` */
export const deleteActivityType = async (id: string): Promise<void> => {
  await apiClient.delete(`/activity-types/${id}`);
};

/** `GET /activity-types/export` — download XLSX. */
export const exportActivityTypesExcel = async (): Promise<void> => {
  const response = await apiClient.get('/activity-types/export', { responseType: 'blob' });
  downloadXlsx(response.data, `activity-types-${new Date().toISOString().slice(0, 10)}.xlsx`);
};
